import React, { useEffect, useRef, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Document, Page, pdfjs } from "react-pdf";
import { getReportById } from "../services/reportsService";
import { isSupabaseConfigured } from "../supabase/client";
import { FALLBACK_REPORTS } from "../data/fallbackReports";
import { REPORT_ARTICLES } from "../data/reportArticles";
import { fyShort } from "../utils/fiscal";
import "./ReportReader.css";

// PDF.js worker — bundled same-origin by webpack (a cross-origin CDN worker
// can't load as a classic Worker, which makes rendering hang).
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.js",
  import.meta.url
).toString();

// Normalize a report from either source into { image, pdf, ... }.
const norm = (r) =>
  r ? { ...r, image: r.image || r.imageUrl, pdf: r.file || r.pdfUrl } : null;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const MAX_PAGE_WIDTH = 820;

const ReportReader = () => {
  const { id } = useParams();
  const location = useLocation();
  const passed = location.state?.report;

  const [report, setReport] = useState(passed ? norm(passed) : null);
  const [status, setStatus] = useState(passed ? "ready" : "loading");

  // PDF rendering state
  const docRef = useRef(null);
  const [pageWidth, setPageWidth] = useState(MAX_PAGE_WIDTH);
  const [numPages, setNumPages] = useState(null);
  const [renderedCount, setRenderedCount] = useState(0);
  // If inline page rendering fails or stalls, fall back to an embedded viewer
  // so the report is always readable in-site (never a blank screen).
  const [useEmbedFallback, setUseEmbedFallback] = useState(false);

  useEffect(() => {
    if (passed) {
      setReport(norm(passed));
      setStatus("ready");
      return;
    }
    let cancelled = false;
    (async () => {
      let found = null;
      if (isSupabaseConfigured) {
        try {
          found = await getReportById(id);
        } catch (e) {
          /* fall through to fallback */
        }
      }
      if (!found) found = FALLBACK_REPORTS.find((r) => r.id === id) || null;
      if (!cancelled) {
        setReport(norm(found));
        setStatus(found ? "ready" : "notfound");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, passed]);

  // Fit the rendered pages to the container width (responsive).
  useEffect(() => {
    const measure = () => {
      if (docRef.current) {
        setPageWidth(Math.min(MAX_PAGE_WIDTH, docRef.current.clientWidth));
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [status]);

  // Safety net: if the document loaded but no page has rendered shortly after,
  // switch to the embedded viewer.
  useEffect(() => {
    if (numPages && renderedCount === 0 && !useEmbedFallback) {
      const t = setTimeout(() => {
        if (renderedCount === 0) setUseEmbedFallback(true);
      }, 6000);
      return () => clearTimeout(t);
    }
  }, [numPages, renderedCount, useEmbedFallback]);

  if (status === "loading") {
    return (
      <div className="reader-loading">
        <div className="reader-spinner" />
      </div>
    );
  }

  if (status === "notfound" || !report) {
    return (
      <div className="reader-missing">
        <h2>Report not found</h2>
        <p>This report may have been moved or removed.</p>
        <Link to="/blog" className="reader-back-btn">&larr; Back to all reports</Link>
      </div>
    );
  }

  // Article precedence: content saved in the database (admin-editable, no
  // deploy) → a built-in article (code) → otherwise fall back to the PDF.
  const articleHtml =
    (report.content && report.content.trim()) ||
    REPORT_ARTICLES[`${report.year}-${report.quarter}`];

  return (
    <div className="report-reader">
      {/* ── Editorial hero ── */}
      <section
        className="reader-hero"
        style={report.image ? { backgroundImage: `url('${report.image}')` } : undefined}
      >
        <div className="reader-hero-overlay" />
        <div className="reader-container">
          <Link to="/blog" className="reader-back">&larr; All Reports</Link>
          <motion.div className="reader-hero-content" initial="hidden" animate="visible" variants={fadeUp}>
            <span className="reader-eyebrow">Management Report</span>
            <div className="reader-quarter">
              <span className="rq-q">{report.quarter}</span>
              <span className="rq-y">FY {fyShort(report.year)}</span>
            </div>
            <h1 className="reader-title">{report.title}</h1>
            {report.abstract && <p className="reader-abstract">{report.abstract}</p>}
            <div className="reader-meta">
              {report.authors && (
                <div className="reader-meta-item">
                  <span className="rm-label">Prepared by</span>
                  <span className="rm-value">{report.authors}</span>
                </div>
              )}
              <div className="reader-meta-item">
                <span className="rm-label">Period</span>
                <span className="rm-value">{report.date}</span>
              </div>
              {report.pages && (
                <div className="reader-meta-item">
                  <span className="rm-label">Length</span>
                  <span className="rm-value">{report.pages} pages</span>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Report body ── */}
      <section className="reader-doc-section">
        <div className="reader-container">
          {!articleHtml && (
            <div className="reader-doc-bar">
              <span className="reader-doc-label">The Full Report</span>
              {report.pdf && (
                <a className="reader-download" href={report.pdf} target="_blank" rel="noopener noreferrer">
                  Download PDF <span className="dl-arrow">&darr;</span>
                </a>
              )}
            </div>
          )}

          {articleHtml ? (
            <article className="report-article" dangerouslySetInnerHTML={{ __html: articleHtml }} />
          ) : !report.pdf ? (
            <div className="reader-doc-missing">This report has no attached document.</div>
          ) : useEmbedFallback ? (
            // Embedded fallback — still in-site, no external redirect.
            <div className="reader-embed-frame">
              <iframe
                title={report.title}
                src={`${report.pdf}#toolbar=0&navpanes=0&view=FitH`}
              />
            </div>
          ) : (
            // Primary: render pages as native "sheets".
            <div className="reader-pages" ref={docRef}>
              <Document
                file={report.pdf}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                onLoadError={() => setUseEmbedFallback(true)}
                loading={
                  <div className="reader-doc-loading">
                    <div className="reader-spinner" />
                    <span>Loading report…</span>
                  </div>
                }
                error={null}
              >
                {Array.from({ length: numPages || 0 }, (_, i) => (
                  <div className="reader-page" key={i}>
                    <Page
                      pageNumber={i + 1}
                      width={pageWidth}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      onRenderSuccess={() => setRenderedCount((c) => c + 1)}
                      onRenderError={() => setUseEmbedFallback(true)}
                    />
                  </div>
                ))}
              </Document>
            </div>
          )}
        </div>
      </section>

      {/* ── Footer nav ── */}
      <section className="reader-footer-nav">
        <div className="reader-container reader-footer-row">
          <Link to="/blog" className="reader-back-btn">&larr; Back to all reports</Link>
          {articleHtml && report.pdf && (
            <a className="reader-download" href={report.pdf} target="_blank" rel="noopener noreferrer">
              Download original PDF <span className="dl-arrow">&darr;</span>
            </a>
          )}
        </div>
      </section>
    </div>
  );
};

export default ReportReader;
